import { useEffect, useRef, useState } from 'react';
import { useDataProvider } from 'react-admin';

import type { CrmDataProvider } from '@/providers/types';
import { type GlobalSearchItem } from './globalSearchApi';

export const GLOBAL_SEARCH_DEBOUNCE_MS = 500;
export const GLOBAL_SEARCH_MIN_CHARS = 3;

/**
 * Debounced global project search. No API call until trimmed query length ≥ min chars.
 */
export function useDebouncedGlobalSearch(query: string, enabled: boolean) {
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [results, setResults] = useState<GlobalSearchItem[]>([]);
    const [loading, setLoading] = useState(false);
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (!enabled) {
            // Invalidate in-flight fetches so they cannot restore stale results.
            requestIdRef.current += 1;
            setResults([]);
            setLoading(false);
            return;
        }
        const trimmed = query.trim();
        if (trimmed.length < GLOBAL_SEARCH_MIN_CHARS) {
            requestIdRef.current += 1;
            setResults([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const requestId = ++requestIdRef.current;
        const timer = setTimeout(() => {
            void (async () => {
                try {
                    const data = await dataProvider.getGlobalSearch(trimmed);
                    if (requestId !== requestIdRef.current) return;
                    setResults(data);
                } catch {
                    if (requestId !== requestIdRef.current) return;
                    setResults([]);
                } finally {
                    if (requestId === requestIdRef.current) {
                        setLoading(false);
                    }
                }
            })();
        }, GLOBAL_SEARCH_DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
        };
    }, [enabled, query]);

    return { results, loading };
}
